import React from 'react';
import Icon from '../../../components/AppIcon';
import { cn } from '../../../utils/cn';

const PermitTable = ({ permits, selectedPermit, onSelectPermit }) => {
  const getStatusColor = (status) => {
    const colors = {
      'submission': 'bg-primary/8 text-primary border-primary/20',
      'under review': 'bg-warning/10 text-warning border-warning/20',
      'approved': 'bg-success/10 text-success border-success/20',
      'rejected': 'bg-destructive/10 text-destructive border-destructive/20'
    };
    return colors?.[status] || 'bg-muted text-muted-foreground border-border';
  };

  const getStatusIcon = (status) => {
    const icons = {
      'submission': 'Clock',
      'under review': 'Search',
      'approved': 'CheckCircle',
      'rejected': 'XCircle'
    };
    return icons?.[status] || 'FileText';
  };

  const getDeadlineUrgency = (deadline) => {
    const today = new Date();
    const daysUntil = Math.ceil((deadline - today) / (1000 * 60 * 60 * 24));
    
    if (daysUntil < 0) return { label: 'Overdue', color: 'text-destructive', icon: 'AlertTriangle' };
    if (daysUntil <= 7) return { label: `${daysUntil} days`, color: 'text-warning', icon: 'Clock' };
    if (daysUntil <= 30) return { label: `${daysUntil} days`, color: 'text-accent', icon: 'Calendar' };
    return { label: `${daysUntil} days`, color: 'text-muted-foreground', icon: 'Calendar' };
  };

  const formatDate = (date) => {
    if (!date) return '';
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    return dateObj?.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  if (permits?.length === 0) {
    return (
      <div className="bg-card rounded-xl p-8 md:p-12 shadow-sm border border-border text-center">
        <Icon name="FileX" size={48} className="text-muted-foreground mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-foreground mb-2">No permits found</h3>
        <p className="text-muted-foreground">Try adjusting your filters or create a new permit application</p>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-xl shadow-sm border border-border overflow-hidden">
      {/* Desktop Table View */}
      <div className="hidden md:block overflow-x-auto">
        <table className="w-full">
          <thead className="border-b border-border">
            <tr style={{ background: 'linear-gradient(135deg, #1B3A5C 0%, #2D5580 100%)' }}>
              <th className="text-left px-6 py-4 text-xs font-semibold uppercase tracking-wider text-white/90">Project</th>
              <th className="text-left px-6 py-4 text-xs font-semibold uppercase tracking-wider text-white/90">Permit Type</th>
              <th className="text-left px-6 py-4 text-xs font-semibold uppercase tracking-wider text-white/90">Submitted</th>
              <th className="text-left px-6 py-4 text-xs font-semibold uppercase tracking-wider text-white/90">Status</th>
              <th className="text-left px-6 py-4 text-xs font-semibold uppercase tracking-wider text-white/90">Deadline</th>
              <th className="text-left px-6 py-4 text-xs font-semibold uppercase tracking-wider text-white/90">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {permits?.map((permit) => {
              const urgency = getDeadlineUrgency(permit?.deadline);
              const isSelected = selectedPermit?.id === permit?.id;
              
              return (
                <tr 
                  key={permit?.id}
                  className={cn(
                    "hover:bg-muted/50 transition-colors cursor-pointer",
                    isSelected && "bg-primary/5"
                  )}
                  onClick={() => onSelectPermit(permit)}
                >
                  <td className="px-6 py-4 md:py-5">
                    <div>
                      <div className="font-medium text-foreground">{permit?.projectName}</div>
                      <div className="text-sm text-muted-foreground">{permit?.applicationNumber}</div>
                    </div>
                  </td>
                  <td className="px-6 py-4 md:py-5">
                    <span className="text-sm text-foreground">{permit?.permitType}</span>
                  </td>
                  <td className="px-6 py-4 md:py-5">
                    <span className="text-sm text-muted-foreground">{formatDate(permit?.submittedDate)}</span>
                  </td>
                  <td className="px-6 py-4 md:py-5">
                    <span className={cn(
                      "inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-xs font-medium border",
                      getStatusColor(permit?.status)
                    )}>
                      <Icon name={getStatusIcon(permit?.status)} size={12} />
                      {permit?.status?.charAt(0)?.toUpperCase() + permit?.status?.slice(1)}
                    </span>
                  </td>
                  <td className="px-6 py-4 md:py-5">
                    <div className="flex items-center gap-2">
                      <Icon name={urgency?.icon} size={14} className={urgency?.color} />
                      <span className={cn("text-sm font-medium", urgency?.color)}>
                        {urgency?.label}
                      </span>
                    </div>
                    <div className="text-xs text-muted-foreground mt-0.5">
                      {formatDate(permit?.deadline)}
                    </div>
                  </td>
                  <td className="px-6 py-4 md:py-5">
                    <button
                      onClick={(e) => {
                        e?.stopPropagation();
                        onSelectPermit(permit);
                      }}
                      className="min-h-[44px] px-3 flex items-center text-primary hover:text-primary/80 text-sm font-medium"
                    >
                      View Details
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {/* Mobile Card View */}
      <div className="md:hidden divide-y divide-border">
        {permits?.map((permit) => {
          const urgency = getDeadlineUrgency(permit?.deadline);
          const isSelected = selectedPermit?.id === permit?.id;
          
          return (
            <div
              key={permit?.id}
              className={cn(
                "p-4 hover:bg-muted/50 transition-colors cursor-pointer",
                isSelected && "bg-primary/5"
              )}
              onClick={() => onSelectPermit(permit)}
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1">
                  <h4 className="font-medium text-foreground mb-1">{permit?.projectName}</h4>
                  <p className="text-sm text-muted-foreground">{permit?.applicationNumber}</p>
                </div>
                <span className={cn(
                  "inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border",
                  getStatusColor(permit?.status)
                )}>
                  <Icon name={getStatusIcon(permit?.status)} size={12} />
                  {permit?.status?.charAt(0)?.toUpperCase() + permit?.status?.slice(1)}
                </span>
              </div>
              
              <div className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Type:</span>
                  <span className="text-foreground font-medium">{permit?.permitType}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Submitted:</span>
                  <span className="text-foreground">{formatDate(permit?.submittedDate)}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Deadline:</span>
                  <div className="flex items-center gap-2">
                    <Icon name={urgency?.icon} size={14} className={urgency?.color} />
                    <span className={cn("font-medium", urgency?.color)}>
                      {urgency?.label}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default PermitTable;